import type { AnnotationBundle, AnnotationItem } from '/@/types/annotation'
import {
  fromLabelStudioLabelArray,
  toLabelStudioLabelArray,
} from '/@/utils/annotation-label'
import type { LabelStore } from '/@/composables/image-annotator/useLabelStore'

/** Label Studio rectanglelabels 单条 result */
export interface LabelStudioRectangleResultItem {
  original_width: number
  original_height: number
  image_rotation: number
  value: {
    x: number
    y: number
    width: number
    height: number
    rotation: number
    rectanglelabels: string[]
  }
  id: string
  from_name: string
  to_name: string
  type: 'rectanglelabels'
  origin: 'manual' | 'prediction'
}

/** Label Studio polygonlabels / polygon 单条 result */
export interface LabelStudioPolygonResultItem {
  original_width: number
  original_height: number
  image_rotation: number
  value: {
    points: number[][]
    polygonlabels?: string[]
    closed: boolean
  }
  id: string
  from_name: string
  to_name: string
  type: 'polygonlabels' | 'polygon'
  origin: 'manual' | 'prediction'
}

export type LabelStudioResultItem =
  | LabelStudioRectangleResultItem
  | LabelStudioPolygonResultItem

/** 单张图片保存体（不含 project，可选 job_id） */
export interface LabelStudioImageSavePayload {
  image_id: string | number
  job_id?: string
  image_url?: string
  lead_time: number
  result: LabelStudioResultItem[]
  draft_id?: number | null
  parent_prediction: null
  parent_annotation: null
  started_at: string
}

export interface LabelStudioSaveMeta {
  lead_time_seconds: number
  started_at: string
}

export function annotationItemToLabelStudioResult (
  ann: AnnotationItem
): LabelStudioResultItem | null {
  if (ann.shape === 'rectangle' && ann.rect) {
    return {
      original_width: ann.original_width,
      original_height: ann.original_height,
      image_rotation: 0,
      value: {
        x: ann.rect.x,
        y: ann.rect.y,
        width: ann.rect.width,
        height: ann.rect.height,
        rotation: ann.rect.rotation ?? 0,
        rectanglelabels: toLabelStudioLabelArray(ann),
      },
      id: ann.id,
      from_name: 'label',
      to_name: 'image',
      type: 'rectanglelabels',
      origin: ann.origin === 'ai' ? 'prediction' : 'manual',
    }
  }

  if (ann.shape === 'polygon' && ann.points && ann.points.length >= 3) {
    const closed = ann.closed ?? true
    const labelArray = toLabelStudioLabelArray(ann)
    const hasLabel = labelArray.length > 0
    return {
      original_width: ann.original_width,
      original_height: ann.original_height,
      image_rotation: 0,
      value: {
        points: ann.points,
        closed,
        ...(hasLabel ? { polygonlabels: labelArray } : {}),
      },
      id: ann.id,
      from_name: 'label',
      to_name: 'image',
      type: hasLabel ? 'polygonlabels' : 'polygon',
      origin: ann.origin === 'ai' ? 'prediction' : 'manual',
    }
  }

  return null
}

export function labelStudioResultToAnnotationItem (
  item: LabelStudioResultItem
): AnnotationItem | null {
  if (item.type === 'rectanglelabels' && item.value && 'width' in item.value) {
    const labelFields = fromLabelStudioLabelArray(item.value.rectanglelabels)
    return {
      id: item.id,
      shape: 'rectangle',
      ...labelFields,
      origin: item.origin === 'prediction' ? 'ai' : 'manual',
      original_width: item.original_width,
      original_height: item.original_height,
      rect: {
        x: item.value.x,
        y: item.value.y,
        width: item.value.width,
        height: item.value.height,
        rotation: item.value.rotation ?? 0,
      },
    }
  }

  if (
    (item.type === 'polygonlabels' || item.type === 'polygon') &&
    item.value &&
    Array.isArray(item.value.points) &&
    item.value.points.length >= 3
  ) {
    const labelFields = fromLabelStudioLabelArray(item.value.polygonlabels)
    return {
      id: item.id,
      shape: 'polygon',
      ...labelFields,
      origin: item.origin === 'prediction' ? 'ai' : 'manual',
      original_width: item.original_width,
      original_height: item.original_height,
      points: item.value.points,
      closed: item.value.closed ?? true,
    }
  }

  return null
}

function parseLooseLabelStudioResultItem (item: unknown): AnnotationItem | null {
  if (!item || typeof item !== 'object') return null
  const row = item as Record<string, unknown>
  const type = row.type as string | undefined
  const value = row.value as Record<string, unknown> | undefined
  if (!value) return null

  if (type === 'rectanglelabels' && typeof value.width === 'number') {
    return labelStudioResultToAnnotationItem(item as LabelStudioRectangleResultItem)
  }

  if (
    (type === 'polygonlabels' ||
      type === 'polygon' ||
      (typeof type === 'string' && type.includes('polygon'))) &&
    Array.isArray(value.points)
  ) {
    return labelStudioResultToAnnotationItem(item as LabelStudioPolygonResultItem)
  }

  return null
}

/** 从接口/草稿 JSON 解析为内部标注列表 */
export function parseLabelStudioAnnotations (
  raw: unknown,
  fallbackWidth = 0,
  fallbackHeight = 0
): AnnotationItem[] {
  if (!raw || typeof raw !== 'object') return []
  const data = raw as Record<string, unknown>
  const result = Array.isArray(data.result)
    ? data.result
    : Array.isArray(raw)
      ? raw
      : []
  return result
    .map((item) => parseLooseLabelStudioResultItem(item))
    .filter((a): a is AnnotationItem => !!a)
    .map((ann) => ({
      ...ann,
      original_width: ann.original_width || fallbackWidth,
      original_height: ann.original_height || fallbackHeight,
    }))
}

export function bundleToTrainingAnnotationResult (
  bundle: AnnotationBundle,
): LabelStudioResultItem[] {
  return bundle.annotations
    .map(annotationItemToLabelStudioResult)
    .filter((r): r is LabelStudioResultItem => r != null)
}

export function bundleToLabelStudioSavePayload (
  bundle: AnnotationBundle,
  options: LabelStudioSaveMeta & { job_id?: string }
): LabelStudioImageSavePayload {
  const result = bundleToTrainingAnnotationResult(bundle)

  return {
    image_id: bundle.image_id,
    job_id: options.job_id,
    image_url: bundle.image_url,
    lead_time: options.lead_time_seconds,
    result,
    draft_id: null,
    parent_prediction: null,
    parent_annotation: null,
    started_at: options.started_at,
  }
}
